import React, { useEffect, useState, FC } from 'react';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import DeleteIcon from '@material-ui/icons/Delete';
import { AddStayDurationModal } from '../add-stay-duration-modal/add-stay-duration-modal.component';
import type { StayDurationItem } from '../../../../../shared/models/unit-types.model';

export interface StayDurationProps {
  data: {
    stayDuration: StayDurationItem[];
  };
  eventHandlers: {
    updateStayDuration: (stayDuration: StayDurationItem[]) => void;
  };
}

export const StayDuration: FC<StayDurationProps> = ({ data, eventHandlers }) => {
  const isMobileView = useWindowResizer();
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState<boolean>(false);
  const [hasPendingUpdates, setHasPendingUpdates] = useState<boolean>(false);
  const [stayDurations, setStayDurations] = useState<StayDurationItem[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());

  useEffect(() => {
    setStayDurations(data?.stayDuration || []);
    setSelectedIndexes(new Set());
    setHasPendingUpdates(false);
  }, [data?.stayDuration]);

  const toggleRowSelection = (index: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIndexes((prev) => {
      if (stayDurations.length === 0) return prev;
      if (prev.size === stayDurations.length) {
        return new Set();
      }
      return new Set(stayDurations.map((_, index) => index));
    });
  };

  const deleteStayDuration = (index: number) => {
    const newStayDurations = stayDurations.filter((_, itemIndex) => itemIndex !== index);
    setStayDurations(newStayDurations);
    setHasPendingUpdates(true);

    setSelectedIndexes((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<number>();
      prev.forEach((selectedIndex) => {
        if (selectedIndex === index) return;
        if (selectedIndex > index) {
          next.add(selectedIndex - 1);
        } else {
          next.add(selectedIndex);
        }
      });
      return next;
    });
  };

  const deleteSelectedStayDurations = () => {
    if (selectedIndexes.size === 0) return;
    const newStayDurations = stayDurations.filter((_, idx) => !selectedIndexes.has(idx));
    setStayDurations(newStayDurations);
    setHasPendingUpdates(true);
    setSelectedIndexes(new Set());
  };

  const getIsRowSelected = (index: number) => selectedIndexes.has(index);

  const updateMinimumStay = (index: number, minimumStay: string) => {
    const newStayDurations = stayDurations.map((item, itemIndex) =>
      itemIndex === index ? { ...item, minimumStay: parseInt(minimumStay) || 0 } : item
    );
    setStayDurations(newStayDurations);
    setHasPendingUpdates(true);
  };

  const saveMinimumStayUpdates = () => {
    eventHandlers.updateStayDuration(stayDurations);
    setHasPendingUpdates(false);
  };

  return (
    <div className="flex-1 mt-4 pb-8 border-t-creams-100 border-t-2 rounded-b-lg p-5 bg-neutral-0">
      {stayDurations?.length > 0 ? (
        <>
          <div className="font-semibold font-gilroy-medium text-primary-500 text-3xl mb-4 flex justify-between items-center">
            Min Stay Duration
            <div className="flex items-center gap-3">
              <Button
                onClick={() => {
                  if (hasPendingUpdates) saveMinimumStayUpdates();
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                disabled={!hasPendingUpdates}
              >
                Save Update
              </Button>
              <Button
                onClick={() => {
                  if (selectedIndexes.size > 0) setShowBulkDeleteConfirm(true);
                }}
                type={ButtonType.danger}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                disabled={selectedIndexes.size === 0}
              >
                Delete ({selectedIndexes.size})
              </Button>
              <Button
                onClick={() => {
                  setShowAddModal(true);
                }}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                Add
              </Button>
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <TableContainer component={Paper}>
              <Table aria-label="stay duration table">
                <TableHead>
                  <TableRow>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Min Stay (Days)</TableCell>
                    <TableCell>
                      <Button
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleSelectAll();
                        }}
                        type={ButtonType.primaryInverted}
                        size={ButtonSize.small}
                      >
                        {selectedIndexes.size === stayDurations.length ? 'Clear All' : 'Select all'}
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stayDurations.map((item, index) => {
                    const isSelected = getIsRowSelected(index);
                    return (
                      <TableRow
                        key={`${item.from}-${item.to}-${index}`}
                        className="cursor-pointer hover:bg-primary-50"
                        onClick={() => toggleRowSelection(index)}
                      >
                        <TableCell className={isSelected ? 'bg-primary-50' : undefined}>
                          {new Date(item.from).toDateString()}
                        </TableCell>
                        <TableCell className={isSelected ? 'bg-primary-50' : undefined}>
                          {new Date(item.to).toDateString()}
                        </TableCell>
                        <TableCell
                          className={isSelected ? 'bg-primary-50' : undefined}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <InputText
                            data={{
                              value: item.minimumStay?.toString() || ''
                            }}
                            config={{
                              maxRows: 1,
                              type: InputTextType.number
                            }}
                            eventHandlers={{
                              onChangeHandler: (value: string) => {
                                updateMinimumStay(index, value);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <DeleteIcon
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteStayDuration(index);
                            }}
                            className="text-red-500 cursor-pointer hover:text-red-700"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>
      ) : (
        <div>
          <div className="font-semibold font-gilroy-medium text-primary-500 text-3xl mb-4">
            Min Stay Duration
          </div>
          <p className="text-gray-500 mb-4 text-center">
            Set minimum stay requirements for specific date ranges
          </p>
          <Button
            onClick={() => {
              setShowAddModal(true);
              trackEvent('add', { category: '', label: '' });
            }}
            tracking={{
              action: 'add Stay Duration',
              category: '',
              label: ''
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            Add Min Stay Duration
          </Button>
        </div>
      )}

      <AddStayDurationModal
        data={{ stayDuration: stayDurations }}
        config={{ showModal: showAddModal }}
        eventHandlers={{
          denyAction: () => setShowAddModal(false),
          confirmAction: () => setShowAddModal(false),
          addStayDuration: (newDurations: StayDurationItem[]) => {
            eventHandlers.updateStayDuration([...stayDurations, ...newDurations]);
          }
        }}
      />

      <Modal
        config={{ showModal: showBulkDeleteConfirm, fullScreen: false, showClose: false }}
        eventHandlers={{
          closeModalHandler: () => setShowBulkDeleteConfirm(false)
        }}
      >
        <div className="flex flex-col justify-center mx-[10%] min-h-[30vh]">
          <div className="font-nunito font-bold text-[#37454d] text-left text-[1.8rem]">
            Confirmation
          </div>
          <div className="mt-2 font-nunito text-[#37454d] text-left leading-normal text-[1.8rem] mb-5">
            <p>Are you sure you want to delete {selectedIndexes.size} stay duration rule(s)?</p>
          </div>
          <div className="flex flex-row justify-end mt-8">
            <div className="mr-4">
              <Button
                onClick={() => setShowBulkDeleteConfirm(false)}
                type={ButtonType.primaryInverted}
                size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              >
                No
              </Button>
            </div>
            <Button
              onClick={() => {
                deleteSelectedStayDurations();
                setShowBulkDeleteConfirm(false);
              }}
              type={ButtonType.danger}
              size={isMobileView ? ButtonSize.full : ButtonSize.medium}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
